import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  FileText,
  History,
  Mail,
  PauseCircle,
  Pencil,
  Phone,
  UserRound,
  XCircle
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { StyledCard } from './pfPraticaFormShared';

function formatCurrency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatPercent(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `${amount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('it-IT');
}

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('it-IT', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function normalizeBadgeColor(value) {
  return String(value || '').trim().toLowerCase();
}

function isHexColor(value) {
  const s = String(value || '').trim();
  return /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test(s);
}

function hexToRgb(hex) {
  const raw = String(hex || '').trim().replace('#', '');
  if (raw.length === 3) {
    return {
      r: parseInt(raw[0] + raw[0], 16),
      g: parseInt(raw[1] + raw[1], 16),
      b: parseInt(raw[2] + raw[2], 16)
    };
  }
  return {
    r: parseInt(raw.slice(0, 2), 16),
    g: parseInt(raw.slice(2, 4), 16),
    b: parseInt(raw.slice(4, 6), 16)
  };
}

function rgba(rgb, alpha) {
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function getBadgeStyle(color) {
  const c = normalizeBadgeColor(color);
  if (isHexColor(c)) {
    const rgb = hexToRgb(c);
    return {
      border: rgba(rgb, 0.35),
      bg: rgba(rgb, 0.08),
      fg: c
    };
  }

  const map = {
    blue: { border: 'rgba(59, 130, 246, 0.35)', bg: 'rgba(59, 130, 246, 0.08)', fg: '#2563EB' },
    indigo: { border: 'rgba(99, 102, 241, 0.35)', bg: 'rgba(99, 102, 241, 0.08)', fg: '#4F46E5' },
    amber: { border: 'rgba(245, 158, 11, 0.35)', bg: 'rgba(245, 158, 11, 0.08)', fg: '#D97706' },
    green: { border: 'rgba(16, 185, 129, 0.35)', bg: 'rgba(16, 185, 129, 0.08)', fg: '#059669' },
    teal: { border: 'rgba(20, 184, 166, 0.35)', bg: 'rgba(20, 184, 166, 0.08)', fg: '#0D9488' },
    red: { border: 'rgba(239, 68, 68, 0.35)', bg: 'rgba(239, 68, 68, 0.08)', fg: '#DC2626' },
    gray: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.08)', fg: '#475569' },
    grey: { border: 'rgba(100, 116, 139, 0.35)', bg: 'rgba(100, 116, 139, 0.08)', fg: '#475569' }
  };

  return map[c] || map.gray;
}

function getWorkflowActionConfig(action) {
  const normalized = String(action || '').trim().toLowerCase();
  const map = {
    approva: {
      key: 'approva',
      label: 'Approva',
      title: 'Conferma approvazione',
      description: 'La pratica passerà allo stato Approvata. L’azione verrà registrata con utente, data e nota.',
      noteLabel: 'Nota approvazione',
      notePlaceholder: 'Aggiungi eventuali dettagli utili per l’approvazione',
      noteRequired: false,
      tone: {
        color: '#166534',
        bg: 'rgba(34, 197, 94, 0.10)',
        border: 'rgba(34, 197, 94, 0.18)'
      },
      submitLabel: 'Conferma approvazione',
      icon: CheckCircle2
    },
    sospendi: {
      key: 'sospendi',
      label: 'Sospendi',
      title: 'Conferma sospensione',
      description: 'La pratica passerà allo stato Sospesa. Il motivo resta tracciato nello storico decisioni.',
      noteLabel: 'Motivo sospensione',
      notePlaceholder: 'Descrivi il motivo della sospensione',
      noteRequired: true,
      tone: {
        color: '#b45309',
        bg: 'rgba(245, 158, 11, 0.10)',
        border: 'rgba(245, 158, 11, 0.18)'
      },
      submitLabel: 'Conferma sospensione',
      icon: PauseCircle
    },
    rifiuta: {
      key: 'rifiuta',
      label: 'Rifiuta',
      title: 'Conferma rifiuto',
      description: 'La pratica passerà allo stato Rifiutata. Il motivo del rifiuto verrà registrato nello storico.',
      noteLabel: 'Motivo rifiuto',
      notePlaceholder: 'Descrivi in modo chiaro il motivo del rifiuto',
      noteRequired: true,
      tone: {
        color: '#b91c1c',
        bg: 'rgba(239, 68, 68, 0.10)',
        border: 'rgba(239, 68, 68, 0.18)'
      },
      submitLabel: 'Conferma rifiuto',
      icon: XCircle
    }
  };

  return map[normalized] || null;
}

function DataField({ label, value, multiline = false }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.5, fontWeight: 600, letterSpacing: 0.15, fontSize: '0.72rem' }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          minHeight: multiline ? 86 : 36,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          px: 1,
          py: multiline ? 0.95 : 0.75,
          display: 'flex',
          alignItems: multiline ? 'flex-start' : 'center',
          bgcolor: 'background.paper'
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: value && value !== '-' ? 'text.primary' : 'text.secondary',
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
            wordBreak: 'break-word',
            fontSize: multiline ? '0.85rem' : '0.87rem',
            lineHeight: 1.25
          }}
        >
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  );
}

function WorkflowStep({ item, active, completed, last }) {
  const badge = getBadgeStyle(item.colore_badge);
  const color = active || completed ? badge.fg : '#cbd5e1';
  return (
    <Box sx={{ position: 'relative', display: 'flex', gap: 0.9, pb: last ? 0 : 1.15 }}>
      <Box sx={{ width: 14, display: 'flex', justifyContent: 'center', position: 'relative', pt: 0.28 }}>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: active || completed ? color : '#d1d5db',
            boxShadow: active ? `0 0 0 2px ${badge.bg}` : 'none',
            zIndex: 1
          }}
        />
        {!last ? (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              bottom: -8,
              width: 2,
              bgcolor: completed ? color : '#e5e7eb'
            }}
          />
        ) : null}
      </Box>
      <Box>
        <Typography
          variant="body2"
          sx={{ fontWeight: active ? 700 : 500, color: active || completed ? color : 'text.primary', fontSize: '0.87rem', lineHeight: 1.2 }}
        >
          {item.nome_visualizzato}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
          {completed ? 'Completato' : active ? 'Stato attuale' : 'In attesa'}
        </Typography>
      </Box>
    </Box>
  );
}

export default function PfPraticaDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pratica, setPratica] = useState(null);
  const [stati, setStati] = useState([]);
  const [documentSummary, setDocumentSummary] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, action: null });
  const [actionForm, setActionForm] = useState({ note: '', data_recall: '' });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [praticaRes, statiRes, documentiRes] = await Promise.all([
          api.get(`/pf-pratiche/${id}`),
          api.get('/pf-stati-pratica'),
          api.get(`/pf-documenti/pratiche/${id}`)
        ]);

        setPratica(praticaRes.data || null);
        const nextStati = Array.isArray(statiRes.data) ? statiRes.data : [];
        setStati(nextStati);
        setDocumentSummary(documentiRes.data?.summary || null);
        setError('');
      } catch (err) {
        console.error('Error fetching pf pratica detail:', err);
        setError(err?.response?.status === 404 ? 'Pratica non trovata' : 'Errore nel caricamento della pratica');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const orderedWorkflow = useMemo(() => {
    return [...stati].sort((a, b) => Number(a.ordinamento || 0) - Number(b.ordinamento || 0));
  }, [stati]);

  const currentWorkflowIndex = useMemo(
    () => orderedWorkflow.findIndex((row) => Number(row.id) === Number(pratica?.stato_id)),
    [orderedWorkflow, pratica?.stato_id]
  );

  const currentBadge = useMemo(
    () => getBadgeStyle(pratica?.stato_colore_badge),
    [pratica?.stato_colore_badge]
  );

  const latestAction = pratica?.ultima_azione || null;
  const workflowActionConfig = useMemo(
    () => getWorkflowActionConfig(actionDialog.action),
    [actionDialog.action]
  );

  const availableActions = useMemo(() => {
    if (!pratica || Number(pratica.stato_finale)) return [];

    return ['approva', 'sospendi', 'rifiuta']
      .map((key) => getWorkflowActionConfig(key))
      .filter(Boolean)
      .filter((item) => {
        if (item.key === 'approva') return pratica.stato_codice !== 'approvata';
        if (item.key === 'sospendi') return pratica.stato_codice !== 'sospesa';
        if (item.key === 'rifiuta') return pratica.stato_codice !== 'rifiutata';
        return true;
      });
  }, [pratica]);

  const handleOpenActionDialog = (action) => {
    setActionDialog({ open: true, action });
    setActionForm({
      note: '',
      data_recall: action === 'sospendi' && pratica?.data_recall ? String(pratica.data_recall).slice(0, 10) : ''
    });
  };

  const handleCloseActionDialog = () => {
    if (actionLoading) return;
    setActionDialog({ open: false, action: null });
    setActionForm({ note: '', data_recall: '' });
  };

  const handleActionSubmit = async () => {
    if (!workflowActionConfig || !pratica?.id) return;

    if (workflowActionConfig.noteRequired && !String(actionForm.note || '').trim()) {
      toast.error('Inserisci una nota prima di confermare l’azione');
      return;
    }

    setActionLoading(true);
    try {
      const payload = {
        action: workflowActionConfig.key,
        note: String(actionForm.note || '').trim()
      };
      if (workflowActionConfig.key === 'sospendi') {
        payload.data_recall = actionForm.data_recall || null;
      }

      const response = await api.post(`/pf-pratiche/${pratica.id}/action`, payload);
      setPratica(response.data || null);
      toast.success(`${workflowActionConfig.label} registrata con successo`);
      handleCloseActionDialog();
    } catch (err) {
      console.error('Error applying pf pratica action:', err);
      toast.error(err?.response?.data?.message || 'Errore durante l’aggiornamento dello stato pratica');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pratica) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Pratica non trovata'}
        </Alert>
        <Button variant="outline" onClick={() => navigate('/pratiche-finanziarie/pratiche')}>
          Torna alla lista
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      <StyledCard sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box
          sx={{
            px: { xs: 1.4, md: 1.8 },
            py: { xs: 1, md: 1.15 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button variant="outline" onClick={() => navigate('/pratiche-finanziarie/pratiche')} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.08, fontSize: '1.05rem' }}>
                {`Pratica #${pratica.id} — ${pratica.cliente_label || 'Cliente'}`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.76rem' }}>
                {`${pratica.tipo_operazione_nome || 'Tipo non definito'} • Aperta il ${formatDate(pratica.data_inserimento)}${pratica.operatore_nome ? ` da ${pratica.operatore_nome}` : ''}`}
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            <Chip
              label={pratica.stato_nome_visualizzato || 'Senza stato'}
              variant="filled"
              sx={{
                bgcolor: currentBadge.bg,
                color: currentBadge.fg,
                border: '1px solid',
                borderColor: currentBadge.border,
                fontWeight: 700
              }}
            />
            {documentSummary && Number(documentSummary.mancanti || 0) > 0 ? (
              <Chip
                label={`${documentSummary.mancanti} doc mancanti`}
                variant="filled"
                sx={{
                  bgcolor: 'rgba(217, 119, 6, 0.12)',
                  color: '#b45309',
                  border: '1px solid',
                  borderColor: 'rgba(217, 119, 6, 0.18)',
                  fontWeight: 700
                }}
              />
            ) : null}
            <Button onClick={() => navigate(`/pratiche-finanziarie/pratiche/${pratica.id}/modifica`)} className="gap-2">
              <Pencil className="h-4 w-4" />
              Modifica
            </Button>
          </Stack>
        </Box>

        <Grid container>
          <Grid item xs={12} md={2.8}>
            <Box sx={{ p: { xs: 1.35, md: 1.6 }, borderRight: { md: '1px solid' }, borderColor: 'divider', height: '100%' }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: 0.35 }}>
                Workflow
              </Typography>
              <Box sx={{ mt: 1.2 }}>
                {orderedWorkflow.map((item, index) => (
                  <WorkflowStep
                    key={item.id}
                    item={item}
                    active={index === currentWorkflowIndex}
                    completed={currentWorkflowIndex >= 0 && index < currentWorkflowIndex}
                    last={index === orderedWorkflow.length - 1}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 1.8 }} />

              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: 0.35 }}>
                Azioni rapide
              </Typography>
              <Stack spacing={0.85} sx={{ mt: 1.2 }}>
                {availableActions.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Button
                      key={item.key}
                      variant="outline"
                      onClick={() => handleOpenActionDialog(item.key)}
                      className="justify-start gap-2"
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Button>
                  );
                })}
                <Button onClick={() => navigate(`/pratiche-finanziarie/pratiche/${pratica.id}/modifica`)} className="justify-start gap-2">
                  <Pencil className="h-4 w-4" />
                  Modifica pratica
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/pratiche-finanziarie/pratiche/${pratica.id}/documenti`)}
                  className="justify-between gap-2"
                >
                  <span className="inline-flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Documenti
                  </span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/pratiche-finanziarie/pratiche')}
                  className="justify-start gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Torna alla lista
                </Button>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={12} md={9.2}>
            <Box sx={{ p: { xs: 1.35, md: 1.6 }, display: 'grid', gap: 1.6 }}>
              {error ? (
                <Alert severity="warning">
                  {error}
                </Alert>
              ) : null}

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.9, fontWeight: 700, fontSize: '0.92rem' }}>
                  Dati cliente
                </Typography>
                <Grid container spacing={1.1}>
                  <Grid item xs={12} md={5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          bgcolor: '#eef2ff',
                          color: '#2563eb',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <UserRound size={14} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.08, fontSize: '0.89rem' }}>
                          {pratica.cliente_label || '-'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.2, fontSize: '0.74rem' }}>
                          {pratica.cliente_codice_fiscale || 'Codice fiscale non disponibile'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={3.5}>
                    <DataField label="Telefono" value={pratica.cliente_telefono || '-'} />
                  </Grid>
                  <Grid item xs={12} md={3.5}>
                    <DataField label="Email" value={pratica.cliente_email || '-'} />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={1.25} sx={{ mt: 0.8 }} useFlexGap flexWrap="wrap">
                  {pratica.cliente_telefono ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                      <Phone size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.79rem' }}>{pratica.cliente_telefono}</Typography>
                    </Box>
                  ) : null}
                  {pratica.cliente_email ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                      <Mail size={14} />
                      <Typography variant="body2" sx={{ fontSize: '0.79rem' }}>{pratica.cliente_email}</Typography>
                    </Box>
                  ) : null}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.9, fontWeight: 700, fontSize: '0.92rem' }}>
                  Dettagli pratica
                </Typography>
                <Grid container spacing={1.1}>
                  <Grid item xs={12} md={6}>
                    <DataField label="Tipo operazione" value={pratica.tipo_operazione_nome || '-'} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DataField label="Istituto erogante" value={pratica.istituto_erogante_nome || '-'} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DataField label="Data scadenza" value={formatDate(pratica.data_scadenza)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DataField label="Data recall" value={formatDate(pratica.data_recall)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DataField label="Data erogazione" value={formatDate(pratica.data_erogazione)} />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.9, fontWeight: 700, fontSize: '0.92rem' }}>
                  Dati finanziari
                </Typography>
                <Grid container spacing={1.1}>
                  <Grid item xs={12} md={3}>
                    <DataField label="Importo richiesto" value={formatCurrency(pratica.importo_richiesto)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DataField label="Importo finanziabile" value={formatCurrency(pratica.importo_finanziabile)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DataField label="Importo erogato" value={formatCurrency(pratica.importo_erogato)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DataField label="Importo rata" value={formatCurrency(pratica.importo_rata)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DataField label="Numero rate" value={pratica.numero_rate ? `${pratica.numero_rate}` : '-'} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DataField label="TAN" value={formatPercent(pratica.tan)} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DataField label="TAEG" value={formatPercent(pratica.taeg)} />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.9, fontWeight: 700, fontSize: '0.92rem' }}>
                  Operativita'
                </Typography>
                <Grid container spacing={1.1}>
                  <Grid item xs={12} md={3}>
                    <DataField label="Operatore inserimento" value={pratica.operatore_nome || '-'} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DataField label="Chiusa da" value={pratica.chiusa_da_nome || '-'} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DataField label="Ultimo aggiornamento" value={formatDateTime(pratica.data_aggiornamento)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DataField
                      label="Ultima decisione"
                      value={
                        latestAction
                          ? `${latestAction.azione_label || latestAction.azione || 'Azione'}${latestAction.eseguita_da_nome ? ` da ${latestAction.eseguita_da_nome}` : ''}`
                          : '-'
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.9, fontWeight: 700, fontSize: '0.92rem', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <History size={16} />
                  Storico decisioni
                </Typography>
                {Array.isArray(pratica.storico_azioni) && pratica.storico_azioni.length ? (
                  <Box sx={{ display: 'grid', gap: 0.85 }}>
                    {pratica.storico_azioni.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                          px: 1.1,
                          py: 0.95
                        }}
                      >
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.8} justifyContent="space-between" useFlexGap>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.2 }}>
                              {item.azione_label || item.azione || 'Azione'}
                              {item.stato_a_nome ? ` · ${item.stato_a_nome}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {`${item.eseguita_da_nome || 'Utente non disponibile'} · ${formatDateTime(item.eseguita_il)}`}
                            </Typography>
                          </Box>
                          {item.stato_da_nome ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', flexShrink: 0 }}>
                              {`Da ${item.stato_da_nome} a ${item.stato_a_nome || '-'}`}
                            </Typography>
                          ) : null}
                        </Stack>
                        {item.data_recall ? (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.6, color: 'text.secondary', fontSize: '0.72rem' }}>
                            {`Recall impostato: ${formatDate(item.data_recall)}`}
                          </Typography>
                        ) : null}
                        {item.nota ? (
                          <Typography sx={{ mt: 0.55, fontSize: '0.79rem', lineHeight: 1.35, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                            {item.nota}
                          </Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
                    Nessuna decisione registrata tramite Approva, Sospendi o Rifiuta.
                  </Alert>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 0.9, fontWeight: 700, fontSize: '0.92rem' }}>
                  Note operative
                </Typography>
                <DataField label="Note" value={pratica.note || 'Nessuna nota operativa inserita'} multiline />
              </Box>

              <Stack direction="row" spacing={0.85} flexWrap="wrap" useFlexGap>
                <Button onClick={() => navigate(`/pratiche-finanziarie/pratiche/${pratica.id}/modifica`)} className="gap-2">
                  <Pencil className="h-4 w-4" />
                  Modifica pratica
                </Button>
                <Button variant="outline" onClick={() => navigate(`/pratiche-finanziarie/pratiche/${pratica.id}/documenti`)} className="gap-2">
                  <FileText className="h-4 w-4" />
                  Apri documenti
                </Button>
                <Button variant="outline" onClick={() => navigate('/pratiche-finanziarie/pratiche')} className="gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Torna alla lista
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>

      <Dialog open={actionDialog.open} onClose={handleCloseActionDialog} fullWidth maxWidth="sm">
        <DialogTitle>{workflowActionConfig?.title || 'Azione pratica'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '14px !important' }}>
          {workflowActionConfig ? (
            <>
              <Alert
                icon={false}
                sx={{
                  border: '1px solid',
                  borderColor: workflowActionConfig.tone.border,
                  bgcolor: workflowActionConfig.tone.bg,
                  color: workflowActionConfig.tone.color
                }}
              >
                <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, lineHeight: 1.35 }}>
                  {workflowActionConfig.description}
                </Typography>
              </Alert>

              <Box
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  p: 1.4,
                  display: 'grid',
                  gap: 0.7
                }}
              >
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  {`Cliente: ${pratica.cliente_label || '-'}`}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  {`Pratica: #${pratica.id} · ${pratica.tipo_operazione_nome || '-'}`}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  {`Stato attuale: ${pratica.stato_nome_visualizzato || '-'}`}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                  {`Registrazione azione: ${user?.name || 'Utente corrente'}`}
                </Typography>
              </Box>

              {workflowActionConfig.key === 'sospendi' ? (
                <TextField
                  label="Data recall"
                  type="date"
                  value={actionForm.data_recall}
                  onChange={(event) => setActionForm((prev) => ({ ...prev, data_recall: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              ) : null}

              <TextField
                label={workflowActionConfig.noteLabel}
                placeholder={workflowActionConfig.notePlaceholder}
                value={actionForm.note}
                onChange={(event) => setActionForm((prev) => ({ ...prev, note: event.target.value }))}
                multiline
                minRows={4}
                fullWidth
                required={workflowActionConfig.noteRequired}
              />
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outline" onClick={handleCloseActionDialog} disabled={actionLoading}>
            Annulla
          </Button>
          <Button onClick={handleActionSubmit} disabled={actionLoading || !workflowActionConfig}>
            {actionLoading ? 'Salvataggio...' : workflowActionConfig?.submitLabel || 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
